import { useState } from "react";
import { FilterSidebarInput } from "./FilterSidebar.types";
import { useFilterSearchParams } from "./useFilterSearchParams";

export function useFilterSidebar<FilterType extends FilterSidebarInput>({
  filters,
  initialFilters,
  enableSearchParamsSync = false,
  onApplyFilters,
  baseUrlForFilteredSearch,
}: UseFilterSidebarProps<FilterType>) {
  const {
    getFiltersFromSearchParams,
    setFiltersInSearchParams,
    clearSearchParams,
    buildListUrlWithSearchParams,
  } = useFilterSearchParams<FilterType>(initialFilters);

  const getInitialFilters = (): FilterType => {
    if (enableSearchParamsSync) {
      const searchParamsFilters = getFiltersFromSearchParams();
      if (searchParamsFilters) {
        return searchParamsFilters;
      }
    }

    if (filters) {
      return filters;
    }

    return initialFilters;
  };

  const [localFilters, setLocalFilters] =
    useState<FilterType>(getInitialFilters);

  const initialize = () => {
    if (enableSearchParamsSync) {
      const queryFilters: Partial<FilterType> = {};
      const searchParamsFilters = getFiltersFromSearchParams();
      if (searchParamsFilters) {
        Object.assign(queryFilters, searchParamsFilters);
      } else {
        Object.assign(queryFilters, initialFilters);
      }
      setLocalFilters(queryFilters as FilterType);
    }
  };

  const setDataFilter = (
    field: keyof FilterType,
    value: string | number | undefined
  ) => {
    setLocalFilters((prevState) => {
      if (prevState[field] === value) {
        return prevState;
      }

      let newValue = value;
      if (value === undefined || value === "") {
        newValue = initialFilters[field];
      }

      if (enableSearchParamsSync) {
        setFiltersInSearchParams({
          ...prevState,
          [field]: newValue,
        } as FilterType);
      }

      return {
        ...prevState,
        [field]: newValue,
      };
    });
  };

  const reset = () => {
    setLocalFilters(initialFilters);
    if (enableSearchParamsSync) {
      clearSearchParams();
    }
  };

  const applyFilters = () => {
    if (baseUrlForFilteredSearch) {
      const queryUrl = buildListUrlWithSearchParams(
        baseUrlForFilteredSearch,
        localFilters
      );

      onApplyFilters(localFilters, queryUrl);
      return;
    }

    onApplyFilters(localFilters);
  };

  return {
    localFilters,
    initialize,
    setDataFilter,
    reset,
    applyFilters,
  };
}

interface UseFilterSidebarProps<FilterType extends FilterSidebarInput> {
  filters: FilterType;
  initialFilters: FilterType;
  enableSearchParamsSync?: boolean;
  groupByDefaultValue?: string | number | undefined;
  sortByDefaultValue?: string | number | undefined;
  dataFiltersDefaultValues?: Partial<FilterType>;
  baseUrlForFilteredSearch?: string;
  onApplyFilters: (filters: FilterType, queryUrl?: string) => void;
}
